/**
 * Goal Plan-Gate Count Algebra (Story 18.10 — AC #7/#8/#10)
 *
 * PURE LEAF MODULE — no React, no fetch (the goalPageDisplay.ts /
 * widgetAccountSelection.ts precedent). A buyer can verify the gate's count
 * algebra without mounting a component. Both the Goals page's "Create Goal"
 * button (free scope) and the create dialog's submit guard (per-client scope)
 * share THIS single count function so the two surfaces never disagree, and so
 * the web count mirrors the server's getGoalCount (apps/server/src/services/
 * plan-limits.ts) exactly.
 *
 * 🔴 DUAL SCOPE (matches the server resolver, AC#3):
 *   - free      → TENANT-WIDE total of every non-demo goal (clientId ignored).
 *   - starter   → PER-CLIENT count of non-demo goals on `clientId`.
 *   - pro       → PER-CLIENT (same as starter).
 *   - agency    → PER-CLIENT (only the 500 safety cap consults it server-side;
 *                 the count here is harmless — agency's goalLimit is null so
 *                 useFeatureGate never blocks below the cap).
 *
 * 🔴 DEMO EXCLUSION (AC#3d / Trap #2): goals whose client is in demo mode are
 * NEVER counted — server parity with the `c.demo_mode = 0` JOIN. Without it a
 * Free user would see seeded "Demo Client N" goals eat their 2-goal allowance.
 *
 * 🔴 LOADING (AC#10): if `goals` or `clients` is undefined (queries still in
 * flight), the count is `undefined`. The page passes that straight into
 * useFeatureGate, whose `checked: false` foot-gun then disables the Create
 * button — creation is NEVER allowed past the gate on an unknown count.
 */

import type { Plan } from "@provenleap/types";

/** Minimal goal shape the gate needs — a subset of GoalListItem. */
export interface GoalGateInput {
  clientId: string;
}

/** Minimal client shape the gate needs — a subset of the clients list. */
export interface ClientGateInput {
  id: string;
  demoMode: boolean;
}

/**
 * Returns the goal count that should be compared against the plan's goalLimit,
 * scoped per the plan, with demo-client goals excluded.
 *
 * @param goals   - the page's loaded goals (undefined while loading)
 * @param clients - the page's loaded clients carrying demoMode (undefined while loading)
 * @param plan    - the tenant's plan (drives the scope)
 * @param clientId - the selected client (required for the per-client scope; when
 *                   omitted on a paid plan the function returns 0 — "no client
 *                   chosen yet", so the page-level button stays enabled and the
 *                   per-client check happens at dialog submit instead)
 * @returns the scoped, demo-excluded count, or `undefined` while inputs load.
 */
export function countGoalsForGate(
  goals: readonly GoalGateInput[] | undefined,
  clients: readonly ClientGateInput[] | undefined,
  plan: Plan,
  clientId?: string,
): number | undefined {
  // AC#10 — unknown count while either query is in flight.
  if (goals === undefined || clients === undefined) return undefined;

  const demoClientIds = new Set(
    clients.filter((c) => c.demoMode).map((c) => c.id),
  );

  const nonDemo = goals.filter((g) => !demoClientIds.has(g.clientId));

  if (plan === "free") {
    // Tenant-wide total — clientId is irrelevant for the free scope.
    return nonDemo.length;
  }

  // Paid tiers are per-client. With no client chosen yet (page-level button on
  // a paid plan), return 0 so the button opens the dialog; the real per-client
  // check fires at submit once a client is selected (AC#8).
  if (clientId === undefined) return 0;

  return nonDemo.filter((g) => g.clientId === clientId).length;
}
